import React, {
  forwardRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
  useContext
} from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import Icons from '../../../icons';
import AppTypeContext from '../../context/AppTypeContext';
import { IconColor, IconSize } from '../Icon/enums';
import Icon from '../Icon/Icon';
import localStyles from './BannerAlert.module.pcss';
import { AppType } from '@aurora/shared-types/app';

export enum BannerAlertVariant {
  INFO = 'info',
  WARNING = 'warning',
  DANGER = 'danger',
  SUCCESS = 'success'
}

export interface BannerAlertProps {
  /**
   * Alert message to display
   */
  alertMessage: string | React.ReactElement;

  /**
   * Variant of the alert to display.
   */
  alertVariant?: BannerAlertVariant;

  /**
   * A data-testid to apply to the banner.
   */
  testId?: string;

  /**
   * The CSS class name(s) that will be applied to the component
   */
  className?: string;
}

/**
 * Inline alert component to be used as feedback on forms
 *
 * @author Andre Almeida
 */
const BannerAlert: ForwardRefExoticComponent<
  React.PropsWithChildren<BannerAlertProps> & RefAttributes<HTMLElement>
> = forwardRef<HTMLElement, BannerAlertProps>(function BannerAlertForward(
  { alertMessage, alertVariant = BannerAlertVariant.DANGER, testId, className },
  ref
) {
  const cx = useClassNameMapper(localStyles);
  const appType = useContext(AppTypeContext);
  const getBannerAlertIcon = () =>
    ({
      [BannerAlertVariant.INFO]: Icons.InfoIcon,
      [BannerAlertVariant.WARNING]: Icons.FlagIcon,
      [BannerAlertVariant.DANGER]: Icons.DangerIcon,
      [BannerAlertVariant.SUCCESS]: Icons.CheckmarkIcon
    }[alertVariant] || Icons.DangerIcon);

  const getBannerAlertAdminIcon = () =>
    ({
      [BannerAlertVariant.INFO]: Icons.InfoAdminIcon,
      [BannerAlertVariant.WARNING]: Icons.FlagAdminIcon,
      [BannerAlertVariant.DANGER]: Icons.DangerAdminIcon,
      [BannerAlertVariant.SUCCESS]: Icons.CheckmarkAdminIcon
    }[alertVariant] || Icons.DangerAdminIcon);

  const getIconColorByVariant = () =>
    ({
      [BannerAlertVariant.INFO]: IconColor.INFO,
      [BannerAlertVariant.WARNING]: IconColor.WARNING,
      [BannerAlertVariant.DANGER]: IconColor.DANGER,
      [BannerAlertVariant.SUCCESS]: IconColor.SUCCESS
    }[alertVariant || IconColor.DANGER]);

  return (
    <span
      data-testid={testId}
      className={cx(
        `lia-alert-container lia-alert-container-${alertVariant} rounded-sm`,
        className
      )}
      ref={ref}
    >
      <div className={cx('lia-alert-icon-wrap')}>
        <Icon
          icon={appType === AppType.ADMIN ? getBannerAlertAdminIcon() : getBannerAlertIcon()}
          color={getIconColorByVariant()}
          size={IconSize.PX_16}
        />
      </div>
      <span className={cx('lia-alert-message')}>{alertMessage}</span>
    </span>
  );
});

export default BannerAlert;
